import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';
import type {
  CrmSearchResponse,
  IngestHealth,
  PersonalContact,
  PersonalContactsResponse,
  PersonalContactSegment,
} from '../types';

// ── CRM Admin → Privacy: personal-contacts list ─────────────────────────────

export interface PersonalContactFilters {
  segment?: PersonalContactSegment;
  search?: string;
  addedBy?: string;
  page?: number;
  pageSize?: number;
}

export function usePersonalContacts(filters: PersonalContactFilters = {}) {
  const params = new URLSearchParams();
  if (filters.segment) params.set('segment', filters.segment);
  if (filters.search) params.set('search', filters.search);
  if (filters.addedBy) params.set('added_by', filters.addedBy);
  if (filters.page != null) params.set('page', String(filters.page));
  if (filters.pageSize != null) params.set('page_size', String(filters.pageSize));
  const qs = params.toString();
  return useQuery<PersonalContactsResponse>({
    queryKey: ['crm-admin', 'personal-contacts', filters],
    queryFn: () =>
      apiFetch<PersonalContactsResponse>(
        `/api/crm/admin/personal-contacts${qs ? `?${qs}` : ''}`,
      ),
    staleTime: 15 * 1000,
  });
}

export interface AddPersonalContactResult {
  id: number;
  email: string;
  messages_resuppressed: number;
  meetings_resuppressed: number;
}

export function useAddPersonalContact() {
  const qc = useQueryClient();
  return useMutation<AddPersonalContactResult, Error, { email: string }>({
    mutationFn: ({ email }) =>
      apiFetch<AddPersonalContactResult>('/api/crm/admin/personal-contacts', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['crm-admin', 'personal-contacts'] }),
  });
}

export interface RemovePersonalContactResult {
  id: number;
  email: string;
  cleared: boolean;
  messages_reelevated: number;
  meetings_reelevated: number;
}

export function useRemovePersonalContact() {
  const qc = useQueryClient();
  return useMutation<RemovePersonalContactResult, Error, PersonalContact>({
    mutationFn: (contact) =>
      apiFetch<RemovePersonalContactResult>(
        `/api/crm/admin/personal-contacts/${contact.id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['crm-admin', 'personal-contacts'] }),
  });
}

// ── CRM Admin → Ingest Health ───────────────────────────────────────────────

/**
 * CRM ingest operational snapshot. Polls while enabled (the server scheduler
 * ticks every 30s, so sub-30s precision isn't useful).
 */
export function useIngestHealth(enabled: boolean = true) {
  return useQuery<IngestHealth>({
    queryKey: ['crm-admin', 'ingest-health'],
    queryFn: () => apiFetch<IngestHealth>('/api/crm/admin/ingest-health'),
    // The snapshot is several queries over the corpus; on a small Azure SQL tier a tight poll +
    // focus-refetch bursts across tabs saturated DTU (prod 2026-06-30). Poll every 60s, don't burst
    // on window focus, and (React Query default) don't poll while the tab is hidden. The ↻ Refresh
    // button covers on-demand needs; the server also coalesces rapid calls via a short TTL cache.
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });
}

// ── CRM Admin → Search (governed Meili search; results are the caller's entitled set) ──────────────

/**
 * Run a governed CRM search. Returns the entitled hits (the Meili tenant token IS the filter — body
 * full-text hits surface only to entitled participants). A mutation (fires on submit, not on type).
 */
export function useCrmSearch() {
  return useMutation<CrmSearchResponse, Error, { query: string; kind?: string }>({
    mutationFn: ({ query, kind }) =>
      apiFetch<CrmSearchResponse>('/api/crm/search', {
        method: 'POST',
        body: JSON.stringify({ query, kind: kind ?? 'content', limit: 50 }),
      }),
  });
}

export interface RequeueIngestResult {
  requeued: number;
  unit_keys: string[];
}

/**
 * Requeue dead-lettered (and optionally stalled) CRM ingest units → pending with attempts reset, so
 * the next cycle re-attempts them. `unitKeys` omitted ⇒ requeue ALL eligible. Invalidates the health
 * snapshot on success so the panel reflects the new state.
 */
export function useRequeueIngest() {
  const qc = useQueryClient();
  return useMutation<RequeueIngestResult, Error, { unitKeys?: string[]; includeStalled?: boolean }>({
    mutationFn: ({ unitKeys, includeStalled }) =>
      apiFetch<RequeueIngestResult>('/api/crm/admin/ingest/requeue', {
        method: 'POST',
        body: JSON.stringify({ unit_keys: unitKeys ?? null, include_stalled: includeStalled ?? false }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'ingest-health'] }),
  });
}

// ── CRM Admin → Backload jobs (CUT-10 archive runner) ─────────────────────────────────────────────

export interface ArchiveKindDetail {
  scopes: number;
  scopes_complete: number;
  /** How many scopes SHOULD exist, where the server can know it (e.g. one per archived Affinity
   *  list). Null/absent = unknowable, so fall back to the started ones. Progress must be read
   *  against this: "1/1 started scopes" is how an archive that had walked 1 of 60 lists read as
   *  finished. */
  scopes_expected?: number | null;
  pages: number;
  rows_seen: number;
  walked: boolean;
}

export interface ArchiveSourceStatus {
  description: string;
  kinds: string[];
  /** A DERIVED job (it reads the archive rather than a remote source), so finishing once is not the
   *  end of it — it must be re-runnable every time the archive grows. */
  rerunnable?: boolean;
  /** Set when the OWNER decided not to archive this source, carrying the reason. Renders as a
   *  decision rather than as "Not finished", which reads as a job someone forgot to run. Still
   *  runnable — declining is a decision, not a lock. */
  declined?: string | null;
  walked: boolean;
  detail: Record<string, ArchiveKindDetail>;
}

export interface ArchiveStatus {
  generated_at: string;
  /** Keyed by source name. Driven by the SERVER's registry — never hardcode this list in the UI, so
   *  registering a new backload job server-side makes it appear here with no frontend change. */
  sources: Record<string, ArchiveSourceStatus>;
  census: {
    kinds: Record<string, {
      archived: number; affinity_total: number | null; delta: number | null;
      walked: boolean; scopes: number; scopes_complete: number;
      scopes_expected?: number | null; pages: number;
    }>;
    complete: boolean;
    shortfalls: string[];
    access_scope: string;
  };
  census_table: string;
  lease: { held: boolean; holder?: string; acquired_at?: string; expires_at?: string; expired?: boolean };
}

/**
 * A job's own last return value. Deliberately opaque except for `summary_line`: a job that has
 * numbers worth reading supplies its own sentence, so the panel renders it without knowing anything
 * about that job — the same rule that keeps the job LIST server-owned.
 */
export interface ArchiveRunLast {
  summary_line?: string;
}

export interface ArchivePullResult {
  /** run_source shape */
  source?: string;
  status?: string;
  chunks?: number;
  elapsed_seconds?: number;
  detail?: string;
  last?: ArchiveRunLast | null;
  /** run_all shape */
  sources?: Record<string, {
    source: string; status: string; chunks?: number; last?: ArchiveRunLast | null;
  }>;
  failed?: Record<string, string>;
  complete?: boolean;
}

export function useArchiveStatus() {
  return useQuery<ArchiveStatus>({
    queryKey: ['crm-admin', 'archive-status'],
    queryFn: () => apiFetch<ArchiveStatus>('/api/crm/admin/archive-status'),
    staleTime: 5 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Advance the archive for up to `maxSeconds`, then return progress.
 *
 * Deliberately NOT run-to-completion: each call resumes from the durable cursor, so the caller
 * drives it in bounded slices (the panel's "keep going" loop does exactly that). Invalidates the
 * status snapshot on success so coverage re-renders after every slice.
 */
export function useArchivePull() {
  const qc = useQueryClient();
  return useMutation<ArchivePullResult, Error,
    { source?: string; maxSeconds?: number; pageChunk?: number }>({
    mutationFn: ({ source, maxSeconds, pageChunk }) =>
      apiFetch<ArchivePullResult>('/api/crm/admin/archive-pull', {
        method: 'POST',
        body: JSON.stringify({
          source: source ?? null,
          max_seconds: maxSeconds ?? 55,
          page_chunk: pageChunk ?? 10,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'archive-status'] }),
  });
}

/**
 * Should the panel issue another slice? The whole point of a bounded call is that "not finished" is
 * the NORMAL outcome, so this encodes which statuses mean "more work remains" versus which mean
 * stop — including the ones that must NOT loop (`stalled` will not fix itself; `locked` means
 * someone else holds the lease; a failure needs a human).
 */
export function archiveShouldContinue(r: ArchivePullResult | undefined): boolean {
  if (!r) return false;
  if (r.failed && Object.keys(r.failed).length > 0) return false;
  if (r.sources) {
    // run_all: keep going while any source still has work and none is stuck.
    const states = Object.values(r.sources).map((s) => s.status);
    if (states.some((s) => s === 'stalled' || s === 'locked')) return false;
    return states.some((s) => s === 'time_bounded' || s === 'not_reached');
  }
  return r.status === 'time_bounded';
}

/**
 * Force-free the archive lease after a crashed run left it held.
 *
 * Safe because the lease prevents DUPLICATED WORK, not incorrect work: the pulls are idempotent
 * (entities upsert, events insert-only) and resume from a durable cursor, so the worst case of
 * releasing a live lease is two walks covering the same pages.
 */
export function useReleaseArchiveLease() {
  const qc = useQueryClient();
  return useMutation<{ released: boolean; was_held_by: string | null }, Error, void>({
    mutationFn: () => apiFetch('/api/crm/admin/archive-lease/release', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'archive-status'] }),
  });
}

/**
 * Forget one source's progress so the next run re-walks it — the archive's "one more pass before the
 * subscription ends" affordance.
 *
 * Only ever offered for a source that has FINISHED and is not re-runnable: a derived job already
 * re-runs on demand, and a job still mid-walk has progress worth keeping. Cursors are all that is
 * deleted — archived rows upsert, so the re-walk refreshes them in place.
 */
export function useResetArchiveSource() {
  const qc = useQueryClient();
  return useMutation<
    { source: string; cursors_deleted: number; note: string }, Error, { source: string }>({
    mutationFn: ({ source }) =>
      apiFetch('/api/crm/admin/archive-source/reset', {
        method: 'POST',
        // The server requires this to equal `source` and 400s otherwise. Sent from one variable so
        // the UI cannot construct a request that confirms a DIFFERENT source than it named.
        body: JSON.stringify({ source, confirm_source: source }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'archive-status'] }),
  });
}

// ── CUT-11 pass 2: the entity backload ──────────────────────────────────────────────────────────
// Creating records is a different act from running an import job, so it gets its own surface rather
// than a second button in Backload jobs: a panel where one click creates ~1,200 CRM records is the
// wrong shape for the first operation on this track that creates rather than reads.

export interface BackloadCohortStatus {
  origin: string;
  description: string;
  organization: number;
  person: number;
}

export interface BackloadCoverage {
  cohorts: Record<string, BackloadCohortStatus>;
  /** origin -> {organization, person} — attributed by cohort, never a flat "came from Affinity". */
  created_by_origin: Record<string, Partial<Record<'organization' | 'person', number>>>;
}

export interface BackloadRunResult {
  cohort: string;
  origin: string;
  write: boolean;
  dry_run: boolean;
  how_to_write?: string;
  summary_line?: string;
  by_kind: Record<string, Record<string, number>>;
}

export function useBackloadCoverage() {
  return useQuery<BackloadCoverage>({
    queryKey: ['crm-admin', 'entity-backload'],
    queryFn: () => apiFetch<BackloadCoverage>('/api/crm/admin/entity-backload'),
    // Long, because this is not a cheap read: each cohort's selector scans its whole archived kind
    // (4,283 notes, or every list entry) on every call. It only changes when a write happens, and the
    // mutation invalidates it — so polling it would buy nothing and cost a table scan per tick.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Run one cohort. **`confirmCohort` is what makes it write** — it must equal `cohort`, so a
 * confirmation cannot be carried from one cohort onto another, and the default is a dry run.
 */
export function useRunBackload() {
  const qc = useQueryClient();
  return useMutation<BackloadRunResult, Error,
    { cohort: string; confirmCohort?: string; maxCreates?: number }>({
    mutationFn: ({ cohort, confirmCohort, maxCreates }) =>
      apiFetch<BackloadRunResult>('/api/crm/admin/entity-backload', {
        method: 'POST',
        body: JSON.stringify({
          cohort,
          confirm_cohort: confirmCohort ?? null,
          max_creates: maxCreates ?? 25,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'entity-backload'] }),
  });
}

// ── CUT-11 proper: the notes import ─────────────────────────────────────────────────────────────

export interface NotesImportCoverage {
  archived_notes: number;
  imported_notes: number;
  extrefs: { organization: number; person: number; list_entry: number };
}

export interface NotesImportResult {
  write: boolean;
  dry_run: boolean;
  how_to_write?: string;
  summary_line?: string;
  archived: number;
  imported: number;
  already: number;
  abstained: number;
  empty: number;
  deferred: number;
  unparsable: number;
  unresolved_affiliations?: Record<string, number>;
}

export function useNotesImportStatus() {
  return useQuery<NotesImportCoverage>({
    queryKey: ['crm-admin', 'notes-import'],
    queryFn: () => apiFetch<NotesImportCoverage>('/api/crm/admin/notes-import'),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Import archived Affinity notes as native notes. **`confirm` is what makes it write** — the server
 * compares it against a fixed literal, so a boolean a retry could set is not enough.
 */
export function useRunNotesImport() {
  const qc = useQueryClient();
  return useMutation<NotesImportResult, Error, { confirm?: string; maxNotes?: number }>({
    mutationFn: ({ confirm, maxNotes }) =>
      apiFetch<NotesImportResult>('/api/crm/admin/notes-import', {
        method: 'POST',
        body: JSON.stringify({ confirm: confirm ?? null, max_notes: maxNotes ?? 50 }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'notes-import'] }),
  });
}

// ── CRM Admin → Portfolio companies (portco master record, S0.1) ────────────

export interface PortcoDomain {
  domain: string;
  is_primary: boolean;
}

export interface PortcoCodename {
  codename: string;
  process_kind: 'platform' | 'add_on' | 'exit';
  /**
   * The RAW curated value `process_kind` was derived from (`operations | exit | add_on | none`).
   * Kept because the derivation is lossy — `operations`, `none` and an unrecognised value all
   * derive to `platform` — and S0.2 deletes the source CSV. NULL for a codename that came from
   * somewhere other than that CSV.
   */
  signal_default: string | null;
  active: boolean;
  note: string | null;
}

/**
 * One fund or vehicle. `kind` keeps a flagship fund distinguishable from a co-invest vehicle or an
 * SPV without pretending the taxonomy is settled — the value set is a seeded lookup table, so a new
 * vehicle is a row rather than a deploy.
 */
export interface PortcoFund {
  name: string;
  kind: 'fund' | 'co_invest' | 'spv' | 'other';
  active?: boolean;
  source?: string;
}

export interface PortfolioCompany {
  id: number;
  name: string;
  crm_org_id: number | null;
  /** The FOUNDING platform transaction as a deal ref — never a bare opportunity id. */
  platform_deal_ref: { deal_kind: string; deal_key: string } | null;
  status: 'active' | 'exited' | 'written_off';
  /**
   * FINAL STATE — the vehicle(s) that actually hold this company. A LIST, because the owner ruling
   * of 2026-08-22 is that a record can be associated with more than one fund; the scalar `fund`
   * string S0.1 shipped could not express "Fund I and a co-invest vehicle" at all.
   */
  funds: PortcoFund[];
  /**
   * HYPOTHESIS — the vehicle(s) the FOUNDING TRANSACTION was expected to use. ⛔ A separate field on
   * purpose: "we expected Fund II" is not "Fund II holds this", and a deal can close into a
   * different vehicle than it was underwritten for. Never render it as the company's fund.
   */
  platform_fund_hypothesis: PortcoFund[];
  acquired_on: string | null;
  exited_on: string | null;
  domains: PortcoDomain[];
  codenames: PortcoCodename[];
  /** Always `[]` in S0.1 — WP1's `deal_portco_link` fills it. Shape frozen now on purpose. */
  transactions: unknown[];
}

/**
 * The portco master record. READ-ONLY, and the tables behind it are DARK in S0.1: nothing in the
 * classifier, resolver or vocabulary loader reads them yet. This is the surface the owner checks the
 * seed script's output on before S0.2 repoints the consumers (spec ruling R4).
 *
 * No polling — the population changes only when an operator runs the seed script.
 */
export function usePortfolioCompanies(enabled: boolean = true) {
  return useQuery<{ companies: PortfolioCompany[]; count: number }>({
    queryKey: ['crm-admin', 'portfolio-companies'],
    queryFn: () =>
      apiFetch<{ companies: PortfolioCompany[]; count: number }>(
        '/api/crm/admin/portfolio-companies',
      ),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });
}
